import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/shared/components/ui/table";
import { Button } from "@/shared/components/ui/button";
import { Edit, Trash2, ChevronLeft, ChevronRight } from "lucide-react";
import type { TagResponseDTO } from "../types";
import { Can } from "@/features/auth/components/Can";
import { ACTIONS } from "@/constants/roles";
import { memo } from "react";
import { TagBadge } from "./TagBadge";

interface TagsTableProps {
  tags: TagResponseDTO[];
  loading: boolean;
  /** Índice 0-based da página atual, alinhado ao parâmetro `page` da API. */
  currentPage: number;
  totalPages: number;
  onEdit: (tag: TagResponseDTO) => void;
  onDelete: (tag: TagResponseDTO) => void;
  /** Chamado com o novo índice de página (0-based). Paginação só é exibida se `totalPages > 1`. */
  onPageChange: (page: number) => void;
}

/**
 * Tabela de listagem de tags com paginação e controle de permissão inline.
 *
 * Botões de editar e excluir são envolvidos por `<Can>` — só aparecem para
 * roles com `tags:update` e `tags:delete` respectivamente.
 * A coluna "Tag" exibe o `TagBadge` com a cor real da tag.
 *
 * @example
 * <TagsTable
 *   tags={filteredTags}
 *   loading={loading}
 *   currentPage={currentPage}
 *   totalPages={totalPages}
 *   onEdit={(tag) => handleOpenDialog("edit", tag)}
 *   onDelete={(tag) => handleOpenDialog("delete", tag)}
 *   onPageChange={handlePageChange}
 * />
 */
export const TagsTable = memo(function TagsTable({
  tags,
  loading,
  currentPage,
  totalPages,
  onEdit,
  onDelete,
  onPageChange,
}: TagsTableProps) {
  return (
    <>
      <Table>
        <TableHeader>
          <TableRow>
            <TableHead>Tag</TableHead>
            <TableHead>Descrição</TableHead>
            <TableHead className="text-right">Ações</TableHead>
          </TableRow>
        </TableHeader>
        <TableBody>
          {tags.map((tag) => (
            <TableRow key={tag.id}>
              <TableCell>
                <TagBadge tag={tag} />
              </TableCell>
              <TableCell className="text-muted-foreground">
                {tag.description || "-"}
              </TableCell>
              <TableCell className="text-right">
                <div className="flex justify-end gap-2">
                  <Can permission={ACTIONS.tags.update}>
                    <Button
                      variant="ghost"
                      size="icon-sm"
                      onClick={() => onEdit(tag)}
                      title="Editar"
                      disabled={loading}
                    >
                      <Edit className="w-4 h-4" />
                    </Button>
                  </Can>
                  <Can permission={ACTIONS.tags.delete}>
                    <Button
                      variant="ghost"
                      size="icon-sm"
                      onClick={() => onDelete(tag)}
                      title="Excluir"
                      disabled={loading}
                    >
                      <Trash2 className="w-4 h-4 text-destructive" />
                    </Button>
                  </Can>
                </div>
              </TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>

      {totalPages > 1 && (
        <div className="flex flex-col md:flex-row items-center justify-between mt-4 pt-4 border-t gap-4">
          <div className="text-sm text-muted-foreground">
            Página {currentPage + 1} de {totalPages}
          </div>
          <div className="flex gap-2 items-center flex-wrap justify-center">
            <Button
              variant="outline"
              size="sm"
              onClick={() => onPageChange(currentPage - 1)}
              disabled={currentPage === 0 || loading}
            >
              <ChevronLeft className="w-4 h-4" />
              Anterior
            </Button>

            <div className="flex gap-1 flex-wrap justify-center">
              {Array.from({ length: totalPages }, (_, i) => {
                const showPage =
                  i === 0 ||
                  i === totalPages - 1 ||
                  (i >= currentPage - 1 && i <= currentPage + 1);

                const showEllipsis =
                  (i === 1 && currentPage > 2) ||
                  (i === totalPages - 2 && currentPage < totalPages - 3);

                if (showEllipsis) {
                  return (
                    <span
                      key={i}
                      className="px-2 py-1 text-sm text-muted-foreground"
                    >
                      ...
                    </span>
                  );
                }

                if (!showPage) return null;

                return (
                  <Button
                    key={i}
                    variant={currentPage === i ? "default" : "outline"}
                    size="sm"
                    onClick={() => onPageChange(i)}
                    disabled={loading}
                    className="min-w-10"
                  >
                    {i + 1}
                  </Button>
                );
              })}
            </div>

            <Button
              variant="outline"
              size="sm"
              onClick={() => onPageChange(currentPage + 1)}
              disabled={currentPage === totalPages - 1 || loading}
            >
              Próxima
              <ChevronRight className="w-4 h-4" />
            </Button>
          </div>
        </div>
      )}
    </>
  );
});
